import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { Cancel, Close } from '@material-ui/icons';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { Confluence } from '../interfaces';
import { loadConfluenceArticleHistory } from '../api';

const useStyles = makeStyles({
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
});

interface IProps extends Omit<DialogProps, 'onClose'> {
  article?: Confluence.IArticle | null;
  onClose(): void;
}

const ArticleHistoryDialog = ({ open, article, onClose, ...props }: IProps) => {
  const [{ loading, data: articleHistory }, setData] = useState<{
    loading: boolean;
    error: string | null;
    data: Confluence.IArticleHistory | null;
  }>({ loading: false, error: null, data: null });

  // Global notifications
  const { enqueueSnackbar } = useSnackbar();

  // Styles
  const classes = useStyles();

  // Methods
  const fetchArticleHistory = async () => {
    if (!article?.id) return;

    try {
      setData((prevState) => ({ ...prevState, loading: true }));

      const response = await loadConfluenceArticleHistory(article.id);

      setData((prevState) => ({ ...prevState, data: response }));
    } catch (error) {
      const message = (error as AxiosError).response?.data.message;

      enqueueSnackbar(message, { variant: 'error' });
      setData({ loading: false, error: message, data: null });
    } finally {
      setData((prevState) => ({ ...prevState, loading: false }));
    }
  };

  // Side effects
  useEffect(() => {
    if (!open) return;

    fetchArticleHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Main render
  return (
    <Dialog
      {...props}
      open={open}
      onClose={(event, reason) => reason !== 'backdropClick' && onClose()}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <Box
        sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, pr: 1, pl: 3 }}
      >
        <Typography variant="h6" component="h2">
          <b>История изменений</b>
        </Typography>

        <Tooltip title="Закрыть">
          <IconButton aria-label="Закрыть" onClick={onClose}>
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && (
        <Box className={classes.loader}>
          <CircularProgress />
        </Box>
      )}

      <DialogContent dividers>
        {articleHistory?.length ? (
          <ArticleHistory articleHistory={articleHistory} />
        ) : (
          <Typography variant="h5" align="center">
            История отсутствует
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="contained" disabled={loading} onClick={onClose} startIcon={<Cancel />}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface IArticleHistoryProps {
  articleHistory: Confluence.IArticleHistory;
}

const ArticleHistory = ({ articleHistory }: IArticleHistoryProps) => {
  const [articleHistoryIndex, setArticleHistoryIndex] = useState(1);

  const { html, name, createBy, hide, updateAt, updateBy } = articleHistory[articleHistoryIndex - 1];

  return (
    <>
      <Box sx={{ minHeight: `calc(100vh - 400px)` }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h2">
              <b>{name}</b>
            </Typography>

            <Typography variant="caption" color="textSecondary">
              Создатель: {createBy}
              {updateBy &&
                `, обновлено ${updateBy}
                    ${updateAt && new Date(updateAt).toLocaleString()}`}
            </Typography>
          </Box>

          {hide && (
            <Box sx={{ ml: 2 }}>
              <Chip label="Скрыта" variant="outlined" size="small" />
            </Box>
          )}
        </Box>

        {html && <Box sx={{ overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: html }} />}
      </Box>

      <Pagination
        page={articleHistoryIndex}
        count={articleHistory.length}
        onChange={(_, index) => setArticleHistoryIndex(index)}
      />
    </>
  );
};

export default ArticleHistoryDialog;
